import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Campaign, CampaignType, CampaignTemplate } from '@dev/ee-api/ee-api-models';
import { isEmpty } from 'lodash-es';
import { safelyDecodeBase64Url } from '@dev/ee-tools';

import { Box } from '@material-ui/core';
import { ResponsiveResizeWrapper } from '@/components/Campaigns/CampaignReport/Overview';

import { AbSplitDisplayMode } from '@/store/campaigns/types';
import {
    CancelPollingCampaignSimpleStats,
    GetCampaignByName,
    GetLocationStatsByCountryIso,
    getCampaignsStore,
} from '@/store/campaigns/actions';
import { ClearCampaignsReportsSummary, GetReportsLocationStatsByName, getReportsState } from '@/store/reports/actions';
import { DEFAULT_PER_PAGE_COUNT } from '@/services/constants';
import usePrevious from '@/services/hooks/usePrevious';
import useMobile from '@/services/hooks/useMobile';
import { GeographyToggleBtnValues, WorldMapsEnum } from './CampaignReportGeographyInterface';
import { getIsWinner, isWorldMapSelected } from '../../helperFunctions';
import GeographyWorldMap from './WorldMap';
import ToggleButtons from './ToggleButton';
import SearchAndFilter from './SearchAndFilter';
import TableWithCountry from './TableWithCountry';
import { DisabledTrackingAlert } from '../components/DisabledTrackingAlert';

const GeographyWrapper = styled.div`
    padding-top: 32px;
`;

const getChannelName = (
    isAbSplit: boolean,
    urlChannelName: string,
    campaign?: Campaign,
    currentTemplate?: string,
    displayMode?: AbSplitDisplayMode
) => {
    if (isAbSplit && campaign) {
        if (displayMode === AbSplitDisplayMode.TestResult) {
            return currentTemplate;
        } else {
            const winnerChannel = getIsWinner(campaign.CampaignTemplates) as CampaignTemplate;
            return winnerChannel.Name;
        }
    }

    return safelyDecodeBase64Url(urlChannelName);
};

export const CampaignReportGeography = (): JSX.Element => {
    const dispatch = useDispatch();
    const isMobile = useMobile();
    const { type, name } = useParams<{ name: string; type: CampaignType }>();
    const isAbSplit = type === CampaignType.AbSplit;

    const { pickedCampaign, abSplitHeaderControls, locationCountryStats } = useSelector(getCampaignsStore);
    const { locationStats } = useSelector(getReportsState);
    const { campaign } = pickedCampaign;

    const [selectedMap, setSelectedMap] = useState(WorldMapsEnum.World);
    const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE_COUNT);
    const [page, setPage] = useState(1);
    const [countryStatisticsFormat, setCountryStaticticsFormat] = useState(GeographyToggleBtnValues.Number);
    const [countriesTableData, setCountriesTableData] = useState([]);
    const [searchResult, setSearchResult] = useState([]);

    const { currentTemplate, displayMode: currentDisplayMode } = abSplitHeaderControls;
    const decodedName = getChannelName(isAbSplit, name, campaign, currentTemplate, currentDisplayMode);

    const prevTemplate = usePrevious(currentTemplate);
    const prevDisplayMode = usePrevious(currentDisplayMode);

    useEffect(() => {
        window.scrollTo(0, 0);

        if (isAbSplit && !campaign) {
            dispatch(
                GetCampaignByName({
                    name: decodedName,
                    campaignType: type,
                    isLoadReportsRequired: false,
                    isSimpleStatsRequired: isEmpty(pickedCampaign.simpleStats) && type === CampaignType.AbSplit,
                })
            );
        }
        if (!isAbSplit) {
            dispatch(GetReportsLocationStatsByName({ channelName: decodedName }));
        }

        setCountriesTableData(locationStats.dataSet);

        return () => {
            dispatch(CancelPollingCampaignSimpleStats());
            dispatch(ClearCampaignsReportsSummary());
        };
    }, []);

    useEffect(() => {
        if (currentDisplayMode && currentTemplate) {
            const isWorldMap = isWorldMapSelected(selectedMap);

            if (isWorldMap) {
                if (prevTemplate !== currentTemplate || prevDisplayMode !== currentDisplayMode) {
                    dispatch(GetReportsLocationStatsByName({ channelName: decodedName }));
                    setCountriesTableData(locationStats?.dataSet);
                }
            } else {
                if (prevTemplate !== currentTemplate || prevDisplayMode !== currentDisplayMode) {
                    dispatch(GetLocationStatsByCountryIso({ countryiso: selectedMap, name: decodedName }));
                    setCountriesTableData(locationCountryStats?.dataSetByCountry);
                }
            }
            setPage(1);
        }
    }, [currentTemplate, currentDisplayMode]);

    return (
        <GeographyWrapper>
            <ResponsiveResizeWrapper width="100%">
                {!locationStats.loading && (
                    <Box mb="32px">
                        <DisabledTrackingAlert />
                    </Box>
                )}
                {!isMobile && (
                    <GeographyWorldMap selectedMap={selectedMap} setSelectedMap={setSelectedMap} setPage={setPage} />
                )}
                {!isMobile && <ToggleButtons setCountryStaticticsFormat={setCountryStaticticsFormat} />}
                {isMobile && (
                    <SearchAndFilter
                        countryStatisticsFormat={countryStatisticsFormat}
                        setCountryStatisticsFormat={setCountryStaticticsFormat}
                        setSearchResult={setSearchResult}
                    />
                )}
                <TableWithCountry
                    perPage={perPage}
                    page={page}
                    setPerPage={setPerPage}
                    setPage={setPage}
                    selectedMap={selectedMap}
                    countryStatisticsFormat={countryStatisticsFormat}
                    countriesTableData={countriesTableData}
                    setCountriesTableData={setCountriesTableData}
                    searchResult={searchResult}
                />
            </ResponsiveResizeWrapper>
        </GeographyWrapper>
    );
};

export default CampaignReportGeography;
